import {
  Button,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Textarea,
} from '@heroui/react';
import React, { useState } from 'react';
import {
  EtsyOrderImport,
  ImportEtsyOrdersRequest,
} from '../../types/orderBoard';

interface ImportEtsyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: (request: ImportEtsyOrdersRequest) => Promise<void>;
}

const ImportEtsyModal: React.FC<ImportEtsyModalProps> = ({
  isOpen,
  onClose,
  onConfirm,
}) => {
  const [ordersText, setOrdersText] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const parseOrdersText = (): EtsyOrderImport[] => {
    if (!ordersText.trim()) {
      return [];
    }

    const lines = ordersText.trim().split('\n');
    const orders: EtsyOrderImport[] = [];

    // Try to parse as CSV or simple format
    // Expected format: OrderNumber, CustomerName, ProductName, Amount, Notes
    // Or JSON array
    try {
      // Try JSON first
      const jsonData = JSON.parse(ordersText);
      if (Array.isArray(jsonData)) {
        return jsonData.map((item: any) => ({
          orderNumber:
            item.orderNumber || item.order_number || item['Order Number'] || '',
          customerName:
            item.customerName || item.customer_name || item['Customer Name'],
          productName:
            item.productName || item.product_name || item['Product Name'],
          amount: item.amount || item.price || item['Amount'],
          notes: item.notes || item['Notes'],
        }));
      }
    } catch {
      // Not JSON, try CSV
    }

    // Parse as CSV or line-by-line
    for (const line of lines) {
      if (!line.trim()) continue;

      const parts = line.split(',').map(p => p.trim());
      if (parts.length >= 1) {
        orders.push({
          orderNumber: parts[0] || '',
          customerName: parts[1],
          productName: parts[2],
          amount: parts[3] ? parseFloat(parts[3]) : undefined,
          notes: parts[4],
        });
      }
    }

    return orders;
  };

  const handleConfirm = async () => {
    const orders = parseOrdersText();
    if (orders.length === 0) {
      return;
    }

    setIsSubmitting(true);
    try {
      await onConfirm({ orders });
      handleClose();
    } catch (error) {
      // Error handling is done by parent
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleClose = () => {
    setOrdersText('');
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size='lg'>
      <ModalContent>
        <ModalHeader>Import Etsy Orders</ModalHeader>
        <ModalBody>
          <div className='space-y-4'>
            <p className='text-sm text-gray-600'>
              Paste Etsy order data in CSV format (OrderNumber, CustomerName,
              ProductName, Amount, Notes) or JSON array format.
            </p>

            <Textarea
              label='Order Data'
              placeholder={`OrderNumber, CustomerName, ProductName, Amount, Notes\n12345, John Doe, Miniature Set, 29.99, Rush order\n\nOr JSON:\n[{"orderNumber": "12345", "customerName": "John Doe", ...}]`}
              value={ordersText}
              onChange={e => setOrdersText(e.target.value)}
              rows={10}
              className='font-mono text-sm'
            />

            <div className='text-xs text-gray-500'>
              <p className='font-semibold mb-1'>CSV Format:</p>
              <p>OrderNumber, CustomerName, ProductName, Amount, Notes</p>
              <p className='mt-2 font-semibold mb-1'>JSON Format:</p>
              <p>{`[{"orderNumber": "12345", "customerName": "John Doe", "productName": "Miniature", "amount": 29.99, "notes": "Notes"}]`}</p>
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <Button variant='ghost' onClick={handleClose} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button
            color='primary'
            onClick={handleConfirm}
            disabled={!ordersText.trim() || isSubmitting}
            isLoading={isSubmitting}
          >
            Import Orders
          </Button>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
};

export default ImportEtsyModal;
